import usePrivateAxios from '@/common/api/usePrivateAxios';
import { Mapper } from '@/common/services/mapper';
import { MetaResponse } from '@/common/types/metaResponse';
import { governmentAllocationsDistributionsTransformerResponse } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/utils/governmentAllocationsDistributionsTransformerResponse';
import { CompanyGovernmentAllocationDistribution } from '@/features/privateCompanies/types/companyGovernmentAllocation.type';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

type GovernmentAllocationsDistributionsMetaResponse = MetaResponse & {
  total_distributions_count: number
}

type UseGovernmentAllocationsDistributionsProps<T> = {
  governmentAllocationId?: string;
  filters?: {
    search?: string;
  };
  mapper?: (data: any) => T;
};

export const useGovernmentAllocationsDistributions = <T,>({
  governmentAllocationId,
  filters: { search = '' } = {},
  mapper = Mapper.default<T>(),
}: UseGovernmentAllocationsDistributionsProps<T>) => {
  const privateAxios = usePrivateAxios()

  const { data: { data, meta } = governmentAllocationsDistributionsTransformerResponse<T>(), ...rest } = useInfiniteQuery({
    queryKey: ['governmentAllocationsDistributions', governmentAllocationId, search],
    queryFn: ({ pageParam }) => {
      return privateAxios.get(`/billing/government_allocations/${governmentAllocationId}/distributions`, {
        params: {
          search,
          page: pageParam,
        },
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: {
      data: { data: CompanyGovernmentAllocationDistribution[]; meta: GovernmentAllocationsDistributionsMetaResponse }
    }) => {
      if (lastPage?.data?.meta?.current_page < lastPage?.data?.meta?.total_pages) {
        return lastPage.data.meta.current_page + 1
      }
    },
    select: (data) => {
      return {
        data: mapper(
          data?.pages?.reduce((acc, page) => {
            return [...acc, ...page?.data?.data]
          }, [] as CompanyGovernmentAllocationDistribution[]),
        ),
        meta: data?.pages[data?.pages?.length - 1].data.meta as GovernmentAllocationsDistributionsMetaResponse,
      }
    },
    placeholderData: keepPreviousData,
  });

  return { data, meta, ...rest };
};
